import React from 'react';
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Description as FileIcon,
  HelpOutline,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { DanelFileUploadItemProps } from './types';
import { BankType } from '../../types/generatedTypes';
import { useDanelFileUploadItem } from './useDanelFileUploadItem';
import styles from './DanelFileUploadItem.module.scss';

const DanelFileUploadItem: React.FC<DanelFileUploadItemProps> = ({
  file,
  index,
  onRemove,
  onBankChange,
  disabled = false,
  showBankFeatures = true,
}) => {
  const { selectedBank, handleBankChange, getBankOptions, getBankDisplayName } =
    useDanelFileUploadItem(file, onBankChange);

  // Render status icon based on detection state
  const renderStatusIcon = () => {
    // Don't show icon if bank is already selected
    if (file.bank) {
      return null;
    }

    // Show '?' if file was not detected at all
    if (file.isDetected === false) {
      return (
        <Tooltip
          title="File not recognized - please select bank type"
          placement="top"
          arrow
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <HelpOutline className={styles.fileItem__statusIcon} />
          </Box>
        </Tooltip>
      );
    }

    return null;
  };

  // Render content based on parse status
  const renderContent = () => {
    const parseStatus = file.parseStatus || 'idle';

    // Parsing state: show spinner
    if (parseStatus === 'parsing') {
      return (
        <>
          <FileIcon color="primary" />
          <div className={styles.fileItem__info}>
            <Typography variant="body2" className={styles.fileItem__name}>
              {file.name}
            </Typography>
            <Typography
              variant="caption"
              className={styles.fileItem__progressText}
            >
              Parsing...
            </Typography>
          </div>
          <CircularProgress size={20} className={styles.fileItem__spinner} />
        </>
      );
    }

    // Success state: show checkmark and row count
    if (parseStatus === 'success') {
      return (
        <>
          <FileIcon color="primary" />
          <div className={styles.fileItem__info}>
            <Typography variant="body2" className={styles.fileItem__name}>
              {file.name}
            </Typography>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
            >
              <CheckCircle className={styles.fileItem__successIcon} />
              <Typography
                variant="caption"
                className={styles.fileItem__successText}
              >
                Parsed successfully ({file.rowCount || 0} rows)
              </Typography>
            </Box>
          </div>
        </>
      );
    }

    // Error state: show error icon and message
    if (parseStatus === 'error') {
      return (
        <>
          <FileIcon color="primary" />
          <div className={styles.fileItem__info}>
            <Typography variant="body2" className={styles.fileItem__name}>
              {file.name}
            </Typography>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
            >
              <Error className={styles.fileItem__errorIcon} />
              <Typography
                variant="caption"
                className={styles.fileItem__errorText}
              >
                {file.parseError || 'Parse failed'}
              </Typography>
            </Box>
          </div>
        </>
      );
    }

    // Idle state: show bank selection (if showBankFeatures is true)
    return (
      <>
        <FileIcon color="primary" />
        <div className={styles.fileItem__info}>
          <Typography
            variant="body2"
            className={styles.fileItem__name}
            data-testid={`file-item-name-${index}`}
          >
            {file.name}
          </Typography>
          <Typography variant="caption" className={styles.fileItem__size}>
            {file.size}
          </Typography>
        </div>
        {showBankFeatures && renderStatusIcon()}
        {showBankFeatures && (
          <FormControl size="small" className={styles.fileItem__bankSelect}>
            <Select
              value={selectedBank}
              onChange={e => handleBankChange(e.target.value as BankType)}
              displayEmpty
              variant="outlined"
              disabled={disabled}
              data-testid={`file-item-bank-select-${index}`}
            >
              <MenuItem value="" disabled>
                Select bank
              </MenuItem>
              {getBankOptions().map(bank => (
                <MenuItem key={bank} value={bank}>
                  {getBankDisplayName(bank)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </>
    );
  };

  return (
    <div className={styles.fileItem} data-testid={`file-item-${index}`}>
      {renderContent()}
      {onRemove && (
        <IconButton
          onClick={onRemove}
          size="small"
          className={styles.fileItem__removeButton}
          disabled={disabled}
          data-testid={`file-item-remove-${index}`}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </div>
  );
};

export default DanelFileUploadItem;
